import { z } from 'zod';
import {
  attachmentMetaSchema,
  backupHistorySchema,
  backupScopeSchema,
  boardColumnSchema,
  boardPlacementSchema,
  calendarEventSchema,
  calendarReminderSchema,
  classificationSchema,
  contactSchema,
  followUpLogSchema,
  matrixPlacementSchema,
  noteSchema,
  noteImageSchema,
  notificationSchema,
  recurrenceRuleSchema,
  settingsSchema,
  supervisorReminderSchema,
  taskContactSchema,
  taskSchema,
  utcTimestampSchema,
} from '@/data/db';

export const BACKUP_FORMAT = 'personal-workspace' as const;
/**
 * 目前寫出的備份版本。版本 2 加入 notes；版本 3 加入本機 PNG 的 noteImages。
 */
export const BACKUP_VERSION = 3 as const;

/**
 * 程式讀得懂的所有版本。
 *
 * 這裡刻意用 union 而不是 `z.literal(BACKUP_VERSION)`：後者是精確等於，
 * 一升版就會讓使用者手上**所有舊備份全部匯不進來**，而 import.ts 只擋
 * 「比程式新」的版本，擋不到這種情況。新增版本時把號碼加進這個陣列即可。
 */
export const SUPPORTED_BACKUP_VERSIONS = [1, 2, 3] as const;
export const backupVersionSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

export const classificationsSchema = z.object({
  taskTypes: z.array(classificationSchema),
  sourceTypes: z.array(classificationSchema),
  tags: z.array(classificationSchema),
  contactTypes: z.array(classificationSchema),
  relationships: z.array(classificationSchema),
});

export const boardBundleSchema = z.object({
  columns: z.array(boardColumnSchema),
  placements: z.array(boardPlacementSchema),
  matrixPlacements: z.array(matrixPlacementSchema),
});

/**
 * data-model 10.2 的頂層格式。
 * 每一個集合都必須列在這裡，否則備份會靜默缺資料。
 */
export const backupFileSchema = z.object({
  format: z.literal(BACKUP_FORMAT),
  version: backupVersionSchema,
  scope: backupScopeSchema,
  exportedAt: utcTimestampSchema,
  appVersion: z.string(),
  /** 各集合筆數，供匯入前快速預覽。 */
  counts: z.record(z.string(), z.number().int().nonnegative()),

  tasks: z.array(taskSchema).optional(),
  taskContacts: z.array(taskContactSchema).optional(),
  recurrenceRules: z.array(recurrenceRuleSchema).optional(),
  contacts: z.array(contactSchema).optional(),
  classifications: classificationsSchema.optional(),
  followUpLogs: z.array(followUpLogSchema).optional(),
  supervisorReminders: z.array(supervisorReminderSchema).optional(),
  board: boardBundleSchema.optional(),
  calendarEvents: z.array(calendarEventSchema).optional(),
  calendarReminders: z.array(calendarReminderSchema).optional(),
  notifications: z.array(notificationSchema).optional(),
  attachments: z.array(attachmentMetaSchema).optional(),
  /** 版本 2 才有；版本 1 的備份一律視為沒有筆記。 */
  notes: z.array(noteSchema).optional(),
  /** Version 3: immutable local PNG metadata; bytes live in the ZIP images/ tree. */
  noteImages: z.array(noteImageSchema).optional(),
  settings: settingsSchema.optional(),
  backupHistory: z.array(backupHistorySchema).optional(),
});

export type BackupFile = z.infer<typeof backupFileSchema>;

/** data-model 第 10 節的匯出對照表，程式化版本。 */
export const EXPORT_MATRIX = {
  tasks: ['full', 'tasks'],
  taskContacts: ['full', 'tasks'],
  recurrenceRules: ['full', 'tasks'],
  contacts: ['full', 'tasks'],
  classifications: ['full', 'tasks'],
  followUpLogs: ['full', 'tasks'],
  supervisorReminders: ['full', 'tasks'],
  board: ['full', 'board'],
  calendarEvents: ['full', 'calendar'],
  calendarReminders: ['full', 'calendar'],
  notifications: ['full'],
  attachments: ['full'],
  notes: ['full'],
  noteImages: ['full'],
  settings: ['full'],
  backupHistory: ['full'],
} as const satisfies Record<string, ReadonlyArray<'full' | 'tasks' | 'board' | 'calendar'>>;

export type ExportKey = keyof typeof EXPORT_MATRIX;
